import type { ModerationItem } from '@/api/pending'
import { ago, fmtDate } from '@/lib/format'
import { Avatar, Button, Card, Icon, Pill } from '@/ui'
import { authorName, CATEGORY_LABEL, REASON_LABEL } from './labels'
import styles from './ReviewCard.module.css'
import type { ReviewAction } from './useReviewAction'

interface ReviewCardProps {
  item: ModerationItem
  mode: 'queue' | 'reported'
  onAction: (action: ReviewAction, item: ModerationItem) => void
  onPreview: (item: ModerationItem) => void
}

export function ReviewCard({ item, mode, onAction, onPreview }: ReviewCardProps) {
  const name = authorName(item.author)
  return (
    <Card pad={20} className={styles.card} aria-label={item.title} role="article">
      <div className={styles.header}>
        <Avatar name={name} size={40} />
        <div className={styles.who}>
          <div className={styles.name}>{name}</div>
          <div className={styles.meta}>
            {item.unit.number} · {item.unit.tower.name} · {ago(item.created_at)}
          </div>
        </div>
        <div className={styles.pills}>
          {item.kind === 'poll' && <Pill tone="brand">Enquete</Pill>}
          <Pill>{CATEGORY_LABEL[item.category]}</Pill>
          {mode === 'reported' && item.report_count !== undefined && (
            <Pill tone="rejected">{item.report_count} denúncias</Pill>
          )}
        </div>
      </div>

      <div>
        <h3 className={styles.title}>{item.title}</h3>
        <p className={styles.body}>{item.body}</p>
      </div>

      {item.kind === 'poll' && item.options && (
        <div className={styles.poll}>
          <ul className={styles.options}>
            {item.options.map((option) => (
              <li key={option} className={styles.option}>
                <Icon name="how_to_vote" size={17} color="var(--color-text-tertiary)" />
                {option}
              </li>
            ))}
          </ul>
          {item.ends_at && <div className={styles.meta}>Encerra em {fmtDate(item.ends_at)}</div>}
        </div>
      )}

      {mode === 'reported' && item.top_reason && (
        <div className={styles.reason}>
          <Icon name="error" size={16} />
          Motivo principal: {REASON_LABEL[item.top_reason]} · maior peso de denúncia{' '}
          {item.max_weight}
        </div>
      )}

      <div className={styles.footer}>
        {mode === 'queue' ? (
          <>
            <Button icon="check" onClick={() => onAction('approve', item)}>
              Aprovar e publicar
            </Button>
            <Button variant="secondary" icon="block" onClick={() => onAction('reject', item)}>
              Recusar
            </Button>
            <span className={styles.spacer} />
            <Button variant="ghost" size="s" icon="visibility" onClick={() => onPreview(item)}>
              Pré-visualizar no feed
            </Button>
          </>
        ) : (
          <>
            <Button variant="danger" icon="block" onClick={() => onAction('remove', item)}>
              Remover conteúdo
            </Button>
            <Button variant="secondary" icon="check" onClick={() => onAction('keep', item)}>
              Manter publicado
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}
